import { useState } from "react";
import {
  BrainCircuit,
  CalendarRange,
  Check,
  ChevronsRight,
  GraduationCap,
  ImageIcon,
  Lock,
  PenSquare,
  Plus,
  ScanSearch,
  ScanText,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAiPlanFeatures } from "@/hooks/use-ai-plan-features";
import { getAiEntitlementState } from "@/lib/ai-plan-entitlements";
import { GLASS_TRIGGER_CLASS } from "@/lib/glass-surface-tokens";
import { getToofiModesForSurface } from "@/lib/toofi-modes";
import { cn } from "@/lib/utils";
import type { ToofiProductIntent, ToofiProductMode } from "@/types/agentic-chat";

const MODE_ICONS = {
  learn: GraduationCap,
  practice: BrainCircuit,
  review: ScanSearch,
  plan: CalendarRange,
  create: PenSquare,
} as const;

type ReaderPlusMenuProps = {
  surfaceKind: "reader" | "workspace";
  value: ToofiProductMode | null;
  onModeChange: (mode: ToofiProductMode | null) => void;
  selectedIntent?: ToofiProductIntent | null;
  onIntentSelect?: (intent: ToofiProductIntent | null) => void;
  onSelectText?: () => void;
  onSelectVisual?: () => void;
  side?: "top" | "bottom";
  align?: "start" | "center" | "end";
  className?: string;
  compact?: boolean;
};

export function ReaderPlusMenu({
  surfaceKind,
  value,
  onModeChange,
  selectedIntent,
  onIntentSelect,
  onSelectText,
  onSelectVisual,
  side = "top",
  align = "start",
  className,
  compact = false,
}: ReaderPlusMenuProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { featureFlags } = useAiPlanFeatures();
  const entitlements = getAiEntitlementState(featureFlags);
  const modesLocked = entitlements.tier === "free";
  const modes = getToofiModesForSurface(surfaceKind);
  const isCompactMenu = compact;
  const showReaderContextSection = surfaceKind === "reader" && (onSelectText || onSelectVisual);
  const showWorkspaceContextSection = surfaceKind !== "reader" && (onSelectText || onSelectVisual);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(
            GLASS_TRIGGER_CLASS,
            "inline-flex items-center justify-center p-0 text-slate-600 hover:text-slate-800",
            compact ? "h-7 w-7" : "h-9 w-9",
            className,
          )}
          aria-label="Open Toofi quick actions"
        >
          <Plus className={cn("transition-transform duration-200", compact ? "h-3.5 w-3.5" : "h-[1.05rem] w-[1.05rem]")} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        side={side}
        sideOffset={12}
        collisionPadding={compact ? 16 : 24}
        onOpenAutoFocus={(event) => event.preventDefault()}
        className={cn(
          "z-[150] max-h-[min(78vh,34rem)] w-[16.5rem] overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:w-[17.5rem]",
          compact && "max-h-[min(56vh,17rem)] w-[11.75rem] p-1 sm:w-[12rem]",
        )}
      >
        {showReaderContextSection ? (
          <>
            {!isCompactMenu ? <DropdownMenuLabel>Add context</DropdownMenuLabel> : null}
            {onSelectText ? (
              <DropdownMenuItem
                className={cn(isCompactMenu && "gap-2 px-2 py-1.5")}
                onSelect={() => {
                  onSelectText();
                  setOpen(false);
                }}
              >
                <span className={cn("inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-50/90 text-blue-600 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]", isCompactMenu && "h-6 w-6")}>
                  <ScanText className={cn("h-3.5 w-3.5", isCompactMenu && "h-3 w-3")} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className={cn("truncate text-[11px] font-semibold text-slate-800", isCompactMenu && "text-[10.5px]")}>Select text</p>
                  {!compact ? <p className="mt-0.5 text-[9px] leading-4 text-slate-500">Add a passage from this PDF to the chat.</p> : null}
                </div>
              </DropdownMenuItem>
            ) : null}
            {onSelectVisual ? (
              <DropdownMenuItem
                className={cn(isCompactMenu && "gap-2 px-2 py-1.5")}
                onSelect={() => {
                  onSelectVisual();
                  setOpen(false);
                }}
              >
                <span className={cn("inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-fuchsia-50/90 text-fuchsia-600 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]", isCompactMenu && "h-6 w-6")}>
                  <ImageIcon className={cn("h-3.5 w-3.5", isCompactMenu && "h-3 w-3")} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className={cn("truncate text-[11px] font-semibold text-slate-800", isCompactMenu && "text-[10.5px]")}>Select visual</p>
                  {!compact ? <p className="mt-0.5 text-[9px] leading-4 text-slate-500">Use a chart, diagram, or figure from the page.</p> : null}
                </div>
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuSeparator />
          </>
        ) : null}
        {!isCompactMenu ? <DropdownMenuLabel>Study mode</DropdownMenuLabel> : null}
        {modes.map((mode) => {
          const Icon = MODE_ICONS[mode.key];
          const selected = mode.key === value;
          return (
            <DropdownMenuItem
              className={cn(isCompactMenu && "gap-2 px-2 py-1.5")}
              key={mode.key}
              onSelect={() => {
                if (modesLocked) {
                  navigate("/student/ai-plan");
                  setOpen(false);
                  return;
                }
                onModeChange(selected ? null : mode.key);
                onIntentSelect?.(null);
                setOpen(false);
              }}
            >
              <span className={cn("inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100/90 text-slate-600", isCompactMenu && "h-6 w-6")}>
                <Icon className={cn("h-3.5 w-3.5", isCompactMenu && "h-3 w-3")} />
              </span>
              <div className="min-w-0 flex-1">
                <p className={cn("truncate text-[11px] font-semibold text-slate-800", isCompactMenu && "text-[10.5px]")}>{mode.label}</p>
                {!compact ? <p className="mt-0.5 text-[9px] leading-4 text-slate-500">{mode.compactHint}</p> : null}
              </div>
              {modesLocked ? <Lock className={cn("ml-2 h-3.5 w-3.5 shrink-0 text-slate-400", isCompactMenu && "h-3 w-3")} /> : selected ? <Check className={cn("ml-2 h-3.5 w-3.5 shrink-0 text-blue-600", isCompactMenu && "h-3 w-3")} /> : null}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuItem
          className={cn(isCompactMenu && "gap-2 px-2 py-1.5")}
          onSelect={() => {
            if (modesLocked) {
              navigate("/student/ai-plan");
              setOpen(false);
              return;
            }
            onModeChange("learn");
            onIntentSelect?.(selectedIntent === "step_by_step" ? null : "step_by_step");
            setOpen(false);
          }}
        >
          <span className={cn("inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-50/90 text-blue-600", isCompactMenu && "h-6 w-6")}>
            <ChevronsRight className={cn("h-3.5 w-3.5", isCompactMenu && "h-3 w-3")} />
          </span>
          <div className="min-w-0 flex-1">
            <p className={cn("truncate text-[11px] font-semibold text-slate-800", isCompactMenu && "text-[10.5px]")}>
              {isCompactMenu ? "Step by step" : "Step-by-step lesson"}
            </p>
            {!compact ? <p className="mt-0.5 text-[9px] leading-4 text-slate-500">Teach one guided step at a time.</p> : null}
          </div>
          {modesLocked ? <Lock className={cn("ml-2 h-3.5 w-3.5 shrink-0 text-slate-400", isCompactMenu && "h-3 w-3")} /> : value === "learn" && selectedIntent === "step_by_step" ? <Check className={cn("ml-2 h-3.5 w-3.5 shrink-0 text-blue-600", isCompactMenu && "h-3 w-3")} /> : null}
        </DropdownMenuItem>
        {showWorkspaceContextSection ? (
          <>
            <DropdownMenuSeparator />
            {!isCompactMenu ? <DropdownMenuLabel>Add context</DropdownMenuLabel> : null}
            {onSelectText ? (
              <DropdownMenuItem
                className={cn(isCompactMenu && "gap-2 px-2 py-1.5")}
                onSelect={() => {
                  onSelectText();
                  setOpen(false);
                }}
              >
                <span className={cn("inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/82 text-slate-600 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]", isCompactMenu && "h-6 w-6")}>
                  <ScanText className={cn("h-3.5 w-3.5", isCompactMenu && "h-3 w-3")} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className={cn("truncate text-[11px] font-semibold text-slate-800", isCompactMenu && "text-[10.5px]")}>Select text</p>
                  {!compact ? <p className="mt-0.5 text-[9px] leading-4 text-slate-500">Add a passage to this same chat.</p> : null}
                </div>
              </DropdownMenuItem>
            ) : null}
            {onSelectVisual ? (
              <DropdownMenuItem
                className={cn(isCompactMenu && "gap-2 px-2 py-1.5")}
                onSelect={() => {
                  onSelectVisual();
                  setOpen(false);
                }}
              >
                <span className={cn("inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/82 text-slate-600 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]", isCompactMenu && "h-6 w-6")}>
                  <ImageIcon className={cn("h-3.5 w-3.5", isCompactMenu && "h-3 w-3")} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className={cn("truncate text-[11px] font-semibold text-slate-800", isCompactMenu && "text-[10.5px]")}>Select visual</p>
                  {!compact ? <p className="mt-0.5 text-[9px] leading-4 text-slate-500">Use a chart or diagram from the page.</p> : null}
                </div>
              </DropdownMenuItem>
            ) : null}
          </>
        ) : null}
        {modesLocked ? (
          <p className="px-2.5 pb-1 pt-1.5 text-[10px] font-medium text-slate-500">
            Upgrade on the plans page to unlock study modes.
          </p>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
